// This file contains the interfaces used by the GameStream typescript libraries which are not exposed from library.

//#region remote configuration types.

/**
 * Represents the config override information source and its version.
 */
export interface RemoteOverrideInfo {
    type: OverrideConfigTypeEnum;
    version: string;
}

/** The GFN specific remote config data fetched from RemoteConfig server */
export declare interface RemoteConfigData {
    /**  common fields for all clients, contains PM communication params etc*/
    common?: string;
    /**  ragnarok specific fields */
    ragnarok?: string;
    /**  remote config file version */
    version?: string;
}

/** Format of configuration parameters from GXT server.
 * Example values: {  name: "ragnarok", value: {<all configurations of Ragnarok>}, version:"x.y" }
 */
export declare interface GxtConfigParams {
    name: string;
    value: Object;
    version: string;
}

/** GXT remote configuration data format. */
export declare interface GxtRemoteConfigData {
    debugInfo: {
        revision: string /** version info of the response, refer GxtConfigParams.version for individual configuration version */;
    };
    params: GxtConfigParams[];
}
//#endregion

//#region Telemetry common types and event definitions.
export const enum GdprLevel {
    Functional = "functional",
    Technical = "technical",
    Behavioral = "behavioral"
}

export const enum BooleanType {
    UNDEFINED = "UNDEFINED",
    TRUE = "TRUE",
    FALSE = "FALSE"
}

export const enum OverrideConfigTypeEnum {
    UNKNOWN = "UNKNOWN",
    RCONFIG = "RCONFIG",
    GXT = "GXT",
    LOCAL = "LOCAL",
    OTHER = "OTHER"
}

export const enum HTTPVerb {
    OTHER = "OTHER",
    HEAD = "HEAD",
    DELETE = "DELETE",
    POST = "POST",
    GET = "GET",
    CONNECT = "CONNECT",
    OPTIONS = "OPTIONS",
    PUT = "PUT",
    WS = "WS"
}

export const enum IPVersion {
    UNKNOWN = "UNKNOWN",
    IPV6 = "IPV6",
    IPV4 = "IPV4"
}

export const enum NetworkTypeEnum {
    OTHER = "OTHER",
    ETHERNET = "ETHERNET",
    UNKNOWN = "UNKNOWN",
    WIFI_2_4 = "WIFI_2_4",
    WIFI_5_0 = "WIFI_5_0",
    MOBILE = "MOBILE",
    MOBILE_2G = "MOBILE_2G",
    MOBILE_3G = "MOBILE_3G",
    MOBILE_4G = "MOBILE_4G",
    MOBILE_5G = "MOBILE_5G"
}

export const enum GSModuleName {
    UNKNOWN = "UNKNOWN",
    NVST = "NVST",
    GRID_SERVER = "GRID_SERVER",
    BIFROST_CLIENT_SDK = "BIFROST_CLIENT_SDK",
    RAGNAROK = "RAGNAROK",
    GERONIMO = "GERONIMO",
    STREAM_PLAYER_SDK = "STREAM_PLAYER_SDK"
}

// todo: Fix the 'UNKNWON' typo in next schema version.
export const enum FeatureSupportStatus {
    UNKNWON = "UNKNWON",
    SUPPORTED = "SUPPORTED",
    UNSUPPORTED = "UNSUPPORTED"
}

export const enum ResumeType {
    NONE = "NONE",
    MANUAL = "MANUAL",
    AUTO = "AUTO"
}

export const enum CodecFrameworkType {
    UNKNOWN = "UNKNOWN",
    DXVA = "DXVA",
    MediaCodec = "MediaCodec",
    MFT = "MFT",
    SoftwareDecoder = "SoftwareDecoder",
    VTDecoder = "VTDecoder"
}

export const enum CodecType {
    UNKNOWN = "UNKNOWN",
    H263 = "H263",
    H264 = "H264",
    HEVC = "HEVC",
    MPEG_4_SP = "MPEG_4_SP",
    VP8 = "VP8",
    VP9 = "VP9",
    AV1 = "AV1"
}

// Common GS Telemetry events.

export declare interface GS_Feature {
    featureName: string;
    supported: FeatureSupportStatus;
    defaultEnabled: BooleanType;
    enabled: BooleanType;
    reason: string;
    moduleName: GSModuleName;
    networkType: NetworkTypeEnum;
    overrideConfigType: OverrideConfigTypeEnum;
    overrideConfigVersion: string;
    sessionId: string;
    subSessionId: string;
}

export declare interface GS_SystemAPICallResult {
    action: string;
    label: string;
    duration: number;
    callOutput: string;
    data: number;
    moduleName: GSModuleName;
    networkType: NetworkTypeEnum;
    overrideConfigType: OverrideConfigTypeEnum;
    overrideConfigVersion: string;
    sessionId: string;
    subSessionId: string;
}

export declare interface GS_DebugInfo {
    key1: string;
    key2: string;
    key3: string;
    key4: string;
    key5: string;
    moduleName: GSModuleName;
    networkType: NetworkTypeEnum;
    overrideConfigType: OverrideConfigTypeEnum;
    overrideConfigVersion: string;
    cmsId: string;
    sessionId: string;
    subSessionId: string;
}

export declare interface GS_ExceptionInfo {
    filename: string;
    lineno: number;
    stacktrace: string;
    colno: number;
    handled: BooleanType;
    category: string;
    message: string;
    moduleName: GSModuleName;
    sessionId: string;
    subSessionId: string;
}

export declare interface GS_ClientMetricEvent {
    metricName: string;
    moduleName: GSModuleName;
    valueInt1: number;
    valueInt2: number;
    valueInt3: number;
    valueString: string;
    valueDouble: number;
    networkType: NetworkTypeEnum;
    cmsId: string;
    sessionId: string;
    subSessionId: string;
}

export declare interface GS_ConnectivityCheck {
    zone1: string;
    zone2: string;
    zone1Status: number;
    zone2Status: number;
    traceRouteIP: string;
    traceRouteDestinationReached: BooleanType;
    traceRouteOutput: string;
    result: string;
    connectivityFlags: string;
    sessionId: string;
    subSessionId: string;
    resumeType: ResumeType;
    overrideConfigType: OverrideConfigTypeEnum;
    overrideConfigVersion: string;
    moduleName: GSModuleName;
}

export declare interface GS_Sleep_Event {
    eventSequence: string;
    sleepTime: number;
    error: string;
    timeToSleep: number;
    moduleName: GSModuleName;
    cmsId: string;
    sessionId: string;
    subSessionId: string;
}

export declare interface GS_WsCallResult {
    retryCount: number;
    sessionId: string;
    subSessionId: string;
    serverId: string;
    url: string;
    overrideConfigType: OverrideConfigTypeEnum;
    overrideConfigVersion: string;
    disconnectionReason: string;
    cleanDisconnection: BooleanType;
    networkType: NetworkTypeEnum;
    statusCode: string;
    cmsId: string;
    moduleName: GSModuleName;
}

export declare interface GS_HttpCallResult {
    callDuration: number;
    verb: HTTPVerb;
    sessionId: string;
    subSessionId: string;
    serverId: string;
    url: string;
    overrideConfigType: OverrideConfigTypeEnum;
    overrideConfigVersion: string;
    requestStatusCode: string;
    requestId: string;
    networkType: NetworkTypeEnum;
    statusCode: string;
    cmsId: string;
    moduleName: GSModuleName;
    response?: string;
}

export declare interface GS_PropertyChange_Event {
    propertyName: String;
    moduleName: GSModuleName;
    oldValue: String;
    newValue: String;
    frameCount: number;
    sessionId: String;
    subSessionId: String;
    cmsId: String;
}

export declare interface GS_CodecError_Event {
    isHardwareDecoding: BooleanType;
    codecFrameworkType: CodecFrameworkType;
    codecType: CodecType;
    reason: String;
    systemInfoGuid: String;
}

/////// Event Type Declarations ////////
const GSEventTypes = {
    GridServer_GameLaunch_Request: {
        name: "GridServer_GameLaunch_Request",
        gdprLevel: GdprLevel.Functional
    },
    GS_Feature: {
        name: "GS_Feature",
        gdprLevel: GdprLevel.Functional
    },
    GS_SystemAPICallResult: {
        name: "GS_SystemAPICallResult",
        gdprLevel: GdprLevel.Functional
    },
    GS_DebugInfo: {
        name: "GS_DebugInfo",
        gdprLevel: GdprLevel.Technical
    },
    GS_ExceptionInfo: {
        name: "GS_ExceptionInfo",
        gdprLevel: GdprLevel.Technical
    },
    GS_ClientMetricEvent: {
        name: "GS_ClientMetricEvent",
        gdprLevel: GdprLevel.Functional
    },
    GS_ConnectivityCheck: {
        name: "GS_ConnectivityCheck",
        gdprLevel: GdprLevel.Functional
    },
    GS_Sleep_Event: {
        name: "GS_Sleep_Event",
        gdprLevel: GdprLevel.Technical
    },
    GS_WsCallResult: {
        name: "GS_WsCallResult",
        gdprLevel: GdprLevel.Functional
    },
    GS_HttpCallResult: {
        name: "GS_HttpCallResult",
        gdprLevel: GdprLevel.Functional
    },
    GS_PropertyChange_Event: {
        name: "GS_PropertyChange_Event",
        gdprLevel: GdprLevel.Functional
    },
    GS_CodecError_Event: {
        name: "GS_CodecError_Event",
        gdprLevel: GdprLevel.Functional
    }
};

export class TelemetryEventBase<T> {
    readonly name: string = "";
    readonly gdprLevel: GdprLevel = GdprLevel.Behavioral;
    readonly ts: string;
    parameters: T;
    constructor(info: T) {
        this.parameters = info;
        this.ts = new Date().toISOString();
    }
}

export class GS_FeatureDef extends TelemetryEventBase<GS_Feature> {
    readonly name: string = GSEventTypes.GS_Feature.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_Feature.gdprLevel;
    constructor(info: GS_Feature) {
        super(info);
    }
}

export class GS_SystemAPICallResultDef extends TelemetryEventBase<GS_SystemAPICallResult> {
    readonly name: string = GSEventTypes.GS_SystemAPICallResult.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_SystemAPICallResult.gdprLevel;
    constructor(info: GS_SystemAPICallResult) {
        super(info);
    }
}

export class GS_DebugInfoDef extends TelemetryEventBase<GS_DebugInfo> {
    readonly name: string = GSEventTypes.GS_DebugInfo.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_DebugInfo.gdprLevel;
    constructor(info: GS_DebugInfo) {
        super(info);
    }
}

export class GS_ExceptionInfoDef extends TelemetryEventBase<GS_ExceptionInfo> {
    readonly name: string = GSEventTypes.GS_ExceptionInfo.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_ExceptionInfo.gdprLevel;
    constructor(info: GS_ExceptionInfo) {
        super(info);
    }
}

export class GS_ClientMetricEventDef extends TelemetryEventBase<GS_ClientMetricEvent> {
    readonly name: string = GSEventTypes.GS_ClientMetricEvent.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_ClientMetricEvent.gdprLevel;
    constructor(info: GS_ClientMetricEvent) {
        super(info);
    }
}

export class GS_ConnectivityCheckDef extends TelemetryEventBase<GS_ConnectivityCheck> {
    readonly name: string = GSEventTypes.GS_ConnectivityCheck.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_ConnectivityCheck.gdprLevel;
    constructor(info: GS_ConnectivityCheck) {
        super(info);
    }
}

export class GS_Sleep_EventDef extends TelemetryEventBase<GS_Sleep_Event> {
    readonly name: string = GSEventTypes.GS_Sleep_Event.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_Sleep_Event.gdprLevel;
    constructor(info: GS_Sleep_Event) {
        super(info);
    }
}

export class GS_WsCallResultDef extends TelemetryEventBase<GS_WsCallResult> {
    readonly name: string = GSEventTypes.GS_WsCallResult.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_WsCallResult.gdprLevel;
    constructor(info: GS_WsCallResult) {
        super(info);
    }
}

export class GS_HttpCallResultDef extends TelemetryEventBase<GS_HttpCallResult> {
    readonly name: string = GSEventTypes.GS_HttpCallResult.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_HttpCallResult.gdprLevel;
    constructor(info: GS_HttpCallResult) {
        super(info);
    }
}

export class GS_PropertyChange_EventDef extends TelemetryEventBase<GS_PropertyChange_Event> {
    readonly name: string = GSEventTypes.GS_PropertyChange_Event.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_PropertyChange_Event.gdprLevel;
    constructor(info: GS_PropertyChange_Event) {
        super(info);
    }
}

export class GS_CodecError_EventDef extends TelemetryEventBase<GS_CodecError_Event> {
    readonly name: string = GSEventTypes.GS_CodecError_Event.name;
    readonly gdprLevel: GdprLevel = GSEventTypes.GS_CodecError_Event.gdprLevel;
    constructor(info: GS_CodecError_Event) {
        super(info);
    }
}

//#endregion
